import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import { Checkbox, Row, Col, Card } from 'antd'
import Button from 'components/Button'
import Select from 'components/Select'
import Input from 'components/Form/Input'
import PlusIcon from 'components/icons/PlusIcon'
import ErrorMessage from 'utils/ErrorMessage'
import styled from 'styled-components'

const StyledCard = styled(Card)`
  margin-bottom: 10px;
  border-radius: 6px;
  border: none;
  .ant-card-body {
    padding: 10px;
  }
`

const StyledLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 5px;
  color: #333;

  
`

const Options = [
  { label: '10th', value: 'tenth' },
  { label: '12th/Diploma', value: 'twelfthOrDiploma' },
  { label: 'UG', value: 'ug' },
  { label: 'PG', value: 'pg' },
  { label: 'PG Diploma', value: 'pgDiploma' },
]

const ScoreFilter = forwardRef(
  ({ filterOptions, filterValues, setFilterValues, handleFilterChange }, ref) => {
    const qualificationOptions = Options;
    const scoreEntries = filterValues?.selectScories || [];
    const selectedQualifications = scoreEntries.map(entry => entry.qualification.value);
    const availableQualifications = qualificationOptions.filter(
      opt => !selectedQualifications.includes(opt.value)
    );


    const [comparisonType, setComparisonType] = useState('GTE')
    const [selectedQualification, setSelectedQualification] = useState(null)
    const [percentage, setPercentage] = useState('')
    const [editMode, setEditMode] = useState(null)

    const handleComparisonChange = e => {
      const newComparisonType = e.target.value
      setComparisonType(newComparisonType)

      if (scoreEntries.length > 0) {
        const updatedEntries = scoreEntries.map(entry => ({
          ...entry,
          comparisonType: newComparisonType,
          nameValue: `Score - ${entry.qualification.value} ${newComparisonType === 'GTE' ? '≥' : '≤'} ${entry.percentage}%`,
        }))
        updateScoreEntries(updatedEntries)
      }
    }

    useEffect(() => {
      if (scoreEntries.length > 0) {
        setComparisonType(scoreEntries[0].comparisonType)
      }
    }, [scoreEntries])

    const handleQualificationChange = value => setSelectedQualification(value)

    const handlePercentageChange = e => {
      const value = e.target.value
      if (value === '') return setPercentage('')
      if (!/^\d{1,3}(\.\d{0,2})?$/.test(value)) {
        ErrorMessage('Only up to 2 decimal places allowed.')
        return
      }
      if (parseFloat(value) > 100) {
        ErrorMessage('Percentage cannot exceed 100.')
        return
      }
      setPercentage(value)
    }

    const updateScoreEntries = (entries) => {
      setFilterValues(prev => ({
        ...prev,
        selectedScoreFilterType: entries?.[0]?.comparisonType,
        selectScories: entries
      }))
      handleFilterChange?.(entries)
    }

    const handleAdd = () => {
      if (!selectedQualification || !percentage) return

      const selectedOption = availableQualifications.find(
        option => option.value === selectedQualification
      )

      const newEntry = {
        id: Date.now(),
        qualification: {
          value: selectedQualification,
          label: selectedOption?.label || selectedQualification,
        },
        comparisonType,
        percentage: parseFloat(percentage),
         nameValue: `Score - ${selectedQualification} ${
                comparisonType === 'GTE' ? '≥' : '≤'
              } ${percentage}%`,
        role: 'Score',
      }

      const existingIndex = scoreEntries.findIndex(
        entry => entry.qualification.value === selectedQualification
      )

      let updatedEntries
      if (existingIndex !== -1) {
        updatedEntries = [...scoreEntries]
        updatedEntries[existingIndex] = {
          ...newEntry,
          id: scoreEntries[existingIndex].id,
        }
      } else {
        updatedEntries = [...scoreEntries, newEntry]
      }

      updateScoreEntries(updatedEntries)
      setSelectedQualification(null)
      setPercentage('')
    }

    const handleEdit = id => {
      const entry = scoreEntries.find(e => e.id === id)
      if (!entry) return
      setEditMode(id)
      setSelectedQualification(entry.qualification.value)
      setPercentage(entry.percentage.toString())
      setComparisonType(entry.comparisonType)
    }

    const handleSaveEdit = () => {
      if (!selectedQualification || !percentage) return

      const selectedOption = availableQualifications.find(
        option => option.value === selectedQualification
      )

      const updatedEntries = scoreEntries.map(entry =>
        entry.id === editMode
          ? {
              ...entry,
              qualification: {
                value: selectedQualification,
                label: selectedOption?.label || selectedQualification,
              },
              comparisonType,
              percentage: parseFloat(percentage),
              nameValue: `Score - ${selectedQualification} ${
                comparisonType === 'GTE' ? '≥' : '≤'
              } ${percentage}%`,
              role: 'Score',
            }
          : entry
      )

      updateScoreEntries(updatedEntries)
      setEditMode(null)
      setSelectedQualification(null)
      setPercentage('')
    }

    useImperativeHandle(ref, () => ({
      handleAdd,
      handleSaveEdit,
    }))

    return (
      <div>
        <Row style={{ marginBottom: 15, padding: '10px' }}>
          <Col span={24}>
            <Checkbox
              value="LTE"
              checked={comparisonType === 'LTE'}
              onChange={handleComparisonChange}
              style={{ marginRight: 10 }}
            >
              ≤ Lesser than or Equal to
            </Checkbox>
          </Col>
          <Col span={24}>
            <Checkbox
              value="GTE"
              checked={comparisonType === 'GTE'}
              onChange={handleComparisonChange}
            >
              ≥ Greater than or Equal to
            </Checkbox>
          </Col>
        </Row>

        {scoreEntries
          .reduce((rows, entry, index) => {
            if (index % 2 === 0) rows.push([entry])
            else rows[rows.length - 1].push(entry)
            return rows
          }, [])
          .map((row, i) => (
            <Row gutter={16} key={i}>
              {row.map(entry => (
                <Col span={12} key={entry.id}>
                  <StyledCard>
                    <Row align="middle" justify="space-between">
                      <Col span={10} style={{ fontWeight: 500 }}>
                        {entry.qualification.label}
                      </Col>
                      <Col span={7}>
                        {entry.comparisonType === 'GTE' ? '≥' : '≤'}{' '}
                        {entry.percentage}%
                      </Col>
                      <Col span={7}>
                        <Button.Primary
                          height="30px"
                          text="Edit"
                          onClick={() => handleEdit(entry.id)}
                        />
                      </Col>
                    </Row>
                  </StyledCard>
                </Col>
              ))}
            </Row>
          ))}

        <Row gutter={[8, 16]} style={{ marginTop: 20 }}>
          <Col span={24}>
            <Row>
              <Col span={10}>
                <StyledLabel>Select Qualification</StyledLabel>
              </Col>
              <Col span={8}>
                <StyledLabel>Percentage</StyledLabel>
              </Col>
            </Row>
          </Col>
          <Col span={10}>
            <Select
              placeholder="Select qualification"
              style={{ width: '95%' }}
              value={selectedQualification}
              onChange={handleQualificationChange}
              options={availableQualifications}
            />
          </Col>
          <Col span={8}>
            <Input
              placeholder="e.g. 80"
              style={{ width: '40%' }}
              value={percentage}
              onChange={handlePercentageChange}
              inputMode="decimal"
              pattern="^\d{1,3}(\.\d{0,2})?$"
              suffix="%"
            />
          </Col>
          <Col span={4}>
            <Button.Secondary
              text={editMode ? 'Save' : 'Add'}
              width="100%"
              icon={<PlusIcon color="#3249D7" />}
              dashed="true"
              onClick={editMode ? handleSaveEdit : handleAdd}
            />
          </Col>
        </Row>
      </div>
    )
  }
)

export default ScoreFilter
